import { EditOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Skeleton,
  Space,
} from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import AsyncFeedback from '../components/async-feedback';
import BrandProductsComponent from '../components/brand/products';
import DetailPageHeader from '../components/detail-page-header';
import OperatorSelect from '../components/product-safety/operator-select';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { brandLoad, brandUpdate } from '../reducers/brand/reducer';
import {
  manufacturersLoadAll,
  responsiblePersonsLoadAll,
} from '../reducers/product-safety/reducer';
import { SPACING } from '../theme/tokens';

interface IForm {
  name: string;
  manufacturerUuid: string | null;
  responsiblePersonUuid: string | null;
}

function BrandDetailContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const brandBrand = useSelector((s: RootState) => s.Brand.brand);
  const brandInProcess = useSelector((s: RootState) => s.Brand.inProcess);
  const brandUpdatedAt = useSelector((s: RootState) => s.Brand.updatedAt);
  const safetyOperators = useSelector(
    (s: RootState) => s.Brand.safetyOperators,
  );
  const manufacturers = useSelector(
    (s: RootState) => s.ProductSafety.manufacturers,
  );
  const responsiblePersons = useSelector(
    (s: RootState) => s.ProductSafety.responsiblePersons,
  );

  const { brandUUID } = useParams();
  const [form] = Form.useForm<IForm>();

  useEffect((): void => {
    if (brandUUID) {
      dispatch(brandLoad(brandUUID));
    }
  }, [dispatch, brandUUID]);

  useEffect((): void => {
    dispatch(manufacturersLoadAll());
    dispatch(responsiblePersonsLoadAll());
  }, [dispatch]);

  useEffect((): void => {
    if (brandBrand) {
      form.setFieldsValue({
        name: brandBrand.name,
        manufacturerUuid: safetyOperators?.manufacturerUuid ?? null,
        responsiblePersonUuid: safetyOperators?.responsiblePersonUuid ?? null,
      });
    }
  }, [brandBrand, brandUpdatedAt, safetyOperators, form]);

  const onFinish = useCallback(
    ({ name, manufacturerUuid, responsiblePersonUuid }: IForm): void => {
      if (brandUUID) {
        dispatch(
          brandUpdate(brandUUID, name.trim(), {
            manufacturerUuid: manufacturerUuid ?? null,
            responsiblePersonUuid: responsiblePersonUuid ?? null,
          }),
        );
      }
    },
    [brandUUID, dispatch],
  );

  if (brandBrand === undefined || !brandUUID) {
    return <Skeleton active />;
  }

  if (brandBrand === null) {
    return (
      <Alert
        type="error"
        title="Brand not found"
        description={`Brand UUID: ${brandUUID}`}
        showIcon
      />
    );
  }

  return (
    <>
      <DetailPageHeader
        title={brandBrand.name}
        onReload={(): void => {
          dispatch(brandLoad(brandUUID));
        }}
        isReloadDisabled={brandInProcess}
        uuid={brandUUID}
      />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={SPACING.lg}>
          <Col span={8}>
            <Form.Item
              name="name"
              label="Brand name"
              rules={[{ required: true }, { type: 'string', min: 2 }]}
            >
              <Input prefix={<EditOutlined />} disabled={brandInProcess} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="manufacturerUuid" label="Manufacturer (GPSR)">
              <OperatorSelect
                operators={manufacturers}
                disabled={brandInProcess}
                placeholder="Select manufacturer"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="responsiblePersonUuid"
              label="EU responsible person (GPSR)"
            >
              <OperatorSelect
                operators={responsiblePersons}
                disabled={brandInProcess}
                placeholder="Select responsible person"
              />
            </Form.Item>
          </Col>
        </Row>

        <Space size={SPACING.lg} align="start">
          <Button
            type="primary"
            htmlType="submit"
            disabled={brandInProcess}
            loading={brandInProcess}
          >
            Save
          </Button>
          <AsyncFeedback
            inProcess={brandInProcess}
            updatedAt={brandUpdatedAt}
          />
        </Space>
      </Form>

      <Divider />

      <BrandProductsComponent />
    </>
  );
}

export default BrandDetailContainer;
